<template>
  <span class="pega-break">
    <applicant-print-title
      :entry-company-name="entryCompanyName"
      :user-name="headerUserName"
      :is-duplicated-entries="isDuplicatedEntries"
      :entry-form-type="entryForm"
      :pathway="pathway"
      :updated-date="resumeUpdatedAt"
    />
    <div class="boss-layout boss-layout-gap-between">
      <div>
        <div class="boss-resume-sheet-title">職務経歴書</div>
      </div>
    </div>
    <div class="boss-layout-block">
      <table class="boss-table-4 boss-table-4-responsive pega-th-color-important">
        <tr>
          <td
            class="boss-table-4-space boss-table-4-space-title"
            colspan="5"
          >経験職種</td>
        </tr>
        <tr>
          <th>経験職種</th>
          <td>
            <span
              data-test="text-experienced-occupations"
              v-html="$options.filters.parseText(experiencedOccupationsText)"
            />
          </td>
        </tr>
        <tr>
          <th>経験業種</th>
          <td>
            <span
              data-test="text-experienced-businesses"
              v-html="$options.filters.parseText(experiencedBusinessesText)"
            />
          </td>
        </tr>
        <tr
          v-if="isDisplayExperiencedCompaniesTitle"
          data-test="experienced-companies-title"
        >
          <td
            class="boss-table-4-space boss-table-4-space-title"
            colspan="5"
          >職務経歴</td>
        </tr>
        <template v-for="(company, i) in career.experiencedCompanies">
          <tr :key="`outline-${i}`">
            <th
              :data-test="`head-career-${i}`"
              :rowspan="(isLightEntry) ? 1 : 2"
            >{{ workingPeriod(company) }}</th>
            <td
              :data-test="`text-career-outline-${i}`"
              class="boss-table-4-cell-group-top"
            >{{ company.companyName }}（従業員数{{ company.employees | parseNumberValue('名') }}） {{ company.employmentStatus | parseText('雇用形態') }} 年収{{ company.salary | parseNumberValue('万円') }}</td>
          </tr>
          <tr
            v-if="!isLightEntry"
            :key="`detail-${i}`"
            :class="'display-contents'"
          >
            <td class="boss-table-4-cell-group-bottom">
              <div
                :data-test="`text-career-detail-${i}`"
                class="pega-pre-wrap"
                v-text="company.workNote"
              />
            </td>
          </tr>
        </template>
        <tr v-if="isLightEntry">
          <th>職務経歴補足</th>
          <td>
            <div
              class="pega-pre-wrap"
              data-test="text-career-note"
              v-text="$options.filters.parseText(career.note)"
            />
          </td>
        </tr>
        <tr>
          <td
            class="boss-table-4-space boss-table-4-space-title"
            colspan="5"
          >スキル・経験</td>
        </tr>
        <tr
          v-for="master in skillsMaster"
          :key="master.id"
        >
          <th :data-test="`head-skills-${master.id}`">{{ master.name }}</th>
          <td :data-test="`detail-skills-${master.id}`">
            <template v-if="layeredSkillsByCategory(master.id).length">
              <template v-for="(skill , i) in layeredSkillsByCategory(master.id)">
                <p
                  :key="`${master.id}-${i}`"
                  :data-test="`label-skills-category-${master.id}-${i}`"
                  class="boss-text"
                >{{ skill[0] | parseSkillCategory }}</p>
                <dl
                  v-for="(detail, j) in skill[1]"
                  :key="`${master.id}-${i}-${j}`"
                  class="boss-definition-list"
                >
                  <dt :data-test="`label-skills-subcategory-${master.id}-${i}-${j}`">{{ detail[0] }}</dt>
                  <dd
                    :data-test="`text-skills-${master.id}-${i}-${j}`"
                    v-html="$options.filters.parseSkillDetail(detail[1])"
                  />
                </dl>
              </template>
            </template>
            <template v-else>
              未登録
            </template>
          </td>
        </tr>
        <tr>
          <th data-test="head-other-skill">その他スキルに関する補足事項</th>
          <td>
            <div
              class="pega-pre-wrap"
              data-test="other-skill-data"
              v-html="$options.filters.parseText(skillNote)"
            />
          </td>
        </tr>
      </table>
    </div>
  </span>
</template>

<script>
import ApplicantPrintTitle from '@/components/molecules/ApplicantPrintTitle'
import _ from 'lodash'
import { mapGetters } from 'vuex'
import * as parse from '@/lib/parse'

export default {
  components: {
    ApplicantPrintTitle
  },
  filters: {
    parseText (val, prefix = '', suffix = '') {
      return (val) ? val + suffix : `${prefix}未登録`
    },
    parseNumberValue (value, suffix = '') {
      return parse.numberValue(value, suffix)
    },
    parseSkillCategory (val) {
      return (val) ? `■${val}` : ''
    },
    parseSkillDetail (val) {
      return _(val).map(o => `${o.name}${(o.comment) ? `(${o.comment})` : ''} ${o.experiencedName}`).join('<br>')
    }
  },
  props: {
    isDuplicatedEntries: {
      type: Boolean,
      default: false
    },
    entryForm: {
      type: String,
      default: ''
    },
    pathway: {
      type: Number,
      default: null
    },
    headerUserName: {
      type: String,
      default: ''
    },
    entryId: {
      type: Number,
      required: true
    },
    entryCompanyName: {
      type: String,
      default: ''
    },
    resumeUpdatedAt: {
      type: String,
      default: ''
    },
    experiencedOccupations: {
      type: Array,
      default: () => []
    },
    experiencedBusinesses: {
      type: Array,
      default: () => []
    },
    career: {
      type: Object,
      default: () => {}
    },
    skills: {
      type: Array,
      default: () => []
    },
    isLightEntry: {
      type: Boolean,
      default: false
    },
    isCasualInterviewRequest: {
      type: Boolean,
      default: false
    },
    skillNote: {
      type: String,
      default: ''
    }
  },
  computed: {
    experiencedOccupationsText () {
      return _(this.experiencedOccupations).map(o => {
        return `${o.occupationName} ${this.convertExperienceJobYearLabel(o.experienceYears)}`
      }).join('<br>')
    },
    experiencedBusinessesText () {
      return _(this.experiencedBusinesses).map(o => {
        return (_.isEmpty(o.businessName)) ? o.businessCategoryName : o.businessName
      }).join('<br>')
    },
    workingPeriod () {
      return (work) => {
        if (!work.firstYear && !work.lastYear) return '未登録'
        // 9999が設定されている場合は'現在就業中'とする
        return `${this.toLocalDate(work.firstYear, work.firstMonth)} ～ ${work.lastYear === 9999 ? '現在就業中' : this.toLocalDate(work.lastYear, work.lastMonth)}`
      }
    },
    isDisplayExperiencedCompaniesTitle () {
      return !this.isCasualInterviewRequest || !_.isEmpty(this.career.experiencedCompanies)
    },
    ...mapGetters([
      'skillsMaster'
    ])
  },
  methods: {
    toLocalDate (year, month) {
      return (year && month) ? `${year}年${month}月` : '未登録'
    },
    convertExperienceJobYearLabel (year) {
      if (year === null) return '経験年数未設定'
      if (year === 0) return '1年未満'
      return `${year}年`
    },
    layeredSkillsByCategory (id) {
      return _(this.skills)
        .filter(_.matchesProperty('path[0].id', id))
        .groupBy(_.property('path[1].name'))
        .mapValues(o => _.toPairs(_.groupBy(o, _.property('path[2].name'))))
        .toPairs().value()
    }
  }
}
</script>
